/**
 * 2026-09-22 — Operasyon kokpiti (Genel Bakış) veri katmanı.
 *
 * Three calls, all typed against the shared contracts of this round:
 *   GET  /admin/overview                                   (provider:view_health)
 *        one bounded server aggregate: system strip, gateways, per-provider
 *        health and the server-ranked problem list. The server owns every
 *        status rule; this module only validates + normalises the payload.
 *   GET  /admin/overview/alarms                            (provider:view_health)
 *        cached evaluator state (Redis) for the global alarm banner.
 *   POST /admin/providers/:providerId/parents/retry-problematic
 *        (provider:manage_inventory, mailbox-unit providers only). The SERVER
 *        picks the candidates; `confirm !== true` is an audited dry run.
 *
 * Fail loud, never silently stale: a payload that does not look like the
 * contract (missing generatedAt/providers, or the SPA's index.html answering an
 * un-routed XHR) is surfaced as an error instead of an empty "Sorun yok" page.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from './client.js';

export type OverviewSeverity = 'critical' | 'warn' | 'info';
export type ProviderHealthStatus = 'ok' | 'warn' | 'critical' | 'idle';
export type GatewayHealthStatus = 'ok' | 'down' | 'unknown';
export type WebshareStatus = 'ok' | 'warn' | 'down' | 'unknown';
export type GatewaySignalSource = 'canary' | 'probes' | 'none';
export type ProviderKind = 'alias' | 'mailbox';

export type ProblemCode =
  | 'gateway_down'
  | 'webshare_pool_low'
  | 'runtime_metrics_stale'
  | 'metrics_unavailable'
  | 'provider_no_delivery'
  | 'provider_sellable_zero'
  | 'provider_stale_verification'
  | 'provider_sticky_quarantine'
  | 'provider_stock_gap'
  | 'stuck_orders'
  | 'notifier_unconfigured'
  | 'evaluator_silent';

export type ProblemParams = Readonly<Record<string, string | number | null>>;

export interface OverviewOrders24h {
  readonly total: number;
  readonly delivered: number;
  readonly cancelled: number;
  readonly open: number;
}

export interface OverviewOrderDay {
  /** UTC day, YYYY-MM-DD. */
  readonly day: string;
  readonly total: number;
  readonly delivered: number;
  readonly cancelled: number;
}

export interface OverviewSystem {
  readonly orders24h: OverviewOrders24h;
  readonly ordersDaily7d: readonly OverviewOrderDay[];
  readonly stuckOrders: { readonly expiredLease: number; readonly oldReserved: number };
  readonly webshare: { readonly poolSize: number | null; readonly status: WebshareStatus };
  readonly runtime: { readonly metricsFresh: boolean | null };
  readonly notifier: { readonly emailConfigured: boolean };
  readonly metricsAvailable: boolean;
}

export interface OverviewGateway {
  readonly providerIds: readonly string[];
  readonly host: string;
  readonly port: number;
  readonly status: GatewayHealthStatus;
  readonly since: string | null;
  readonly lastOkAt: string | null;
  readonly source: GatewaySignalSource;
}

export interface OverviewReasonCount {
  readonly code: string;
  readonly count: number;
}

export interface OverviewProviderAccounts {
  readonly total: number;
  readonly active: number;
  readonly verified: number;
  readonly pending: number;
  readonly loginFailed: number;
  readonly sticky: number;
  readonly disabled: number;
  readonly suspended: number;
  readonly noCredential: number;
  readonly lastVerifiedAt: string | null;
}

export interface OverviewProvider {
  readonly providerId: string;
  readonly label: string;
  readonly kind: ProviderKind;
  readonly onSale: boolean;
  readonly activeDomains: number;
  readonly accounts: OverviewProviderAccounts;
  readonly quarantineReasons: readonly OverviewReasonCount[];
  readonly stock: {
    readonly displayed: number;
    readonly sellable: number;
    readonly rawAvailable: number;
    readonly inFlight: number;
  };
  readonly orders24h: { readonly total: number; readonly delivered: number; readonly cancelled: number };
  readonly orders7d: { readonly total: number; readonly delivered: number };
  readonly lastDeliveryAt: string | null;
  readonly renewal24h: { readonly completed: number; readonly failed: number } | null;
  readonly gateway: { readonly status: GatewayHealthStatus; readonly since: string | null } | null;
  readonly status: ProviderHealthStatus;
}

export type OverviewProblemAction =
  | { readonly kind: 'retry_problematic'; readonly providerId: string }
  | { readonly kind: 'link'; readonly href: string }
  | null;

export interface OverviewProblem {
  readonly id: string;
  readonly severity: OverviewSeverity;
  /** A {@link ProblemCode}; kept as string so an unknown future code still renders. */
  readonly code: string;
  readonly providerId: string | null;
  readonly params: ProblemParams;
  readonly since: string | null;
  readonly action: OverviewProblemAction;
}

export interface OverviewResponse {
  readonly generatedAt: string;
  readonly cacheAgeSec: number;
  readonly system: OverviewSystem;
  readonly gateways: readonly OverviewGateway[];
  readonly providers: readonly OverviewProvider[];
  readonly problems: readonly OverviewProblem[];
}

export interface OverviewAlarm {
  readonly id: string;
  readonly severity: 'critical' | 'warn';
  readonly code: string;
  readonly providerIds: readonly string[];
  readonly since: string | null;
  readonly params: ProblemParams;
}

export interface OverviewAlarmsResponse {
  readonly evaluatedAt: string | null;
  readonly evaluatorAlive: boolean;
  readonly alarms: readonly OverviewAlarm[];
  readonly notifier: { readonly emailConfigured: boolean; readonly lastSentAt: string | null };
}

export interface RetryProblematicDryRun {
  readonly dryRun: true;
  readonly candidates: number;
  readonly sticky: number;
  readonly skippedInFlight: number;
  readonly noCredential: number;
}

export interface RetryProblematicApplied {
  readonly dryRun: false;
  readonly requested: number;
  readonly nudged: number;
  readonly skippedInFlight: number;
}

export type RetryProblematicResult = RetryProblematicDryRun | RetryProblematicApplied;

export interface RetryProblematicInput {
  readonly confirm: boolean;
  readonly attempts?: 1 | 2 | 3;
  readonly reason?: string;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const OVERVIEW_PATH = '/admin/overview';
export const OVERVIEW_ALARMS_PATH = '/admin/overview/alarms';

export async function fetchOverview(options: ApiClientOptions): Promise<ApiResult<OverviewResponse>> {
  const res = await callAdminApi<unknown>(options, { path: OVERVIEW_PATH });
  // 503 {error:'overview_unavailable'} → a specific, translatable reason.
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeOverview(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_overview_payload', body: null };
}

export async function fetchOverviewAlarms(
  options: ApiClientOptions,
): Promise<ApiResult<OverviewAlarmsResponse>> {
  const res = await callAdminApi<unknown>(options, { path: OVERVIEW_ALARMS_PATH });
  if (res.kind !== 'ok') return res;
  const data = normalizeAlarms(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_alarms_payload', body: null };
}

export async function retryProblematic(
  options: ApiClientOptions,
  providerId: string,
  input: RetryProblematicInput,
): Promise<ApiResult<RetryProblematicResult>> {
  const body: Record<string, unknown> = { confirm: input.confirm === true };
  if (input.attempts !== undefined) body.attempts = input.attempts;
  if (input.reason !== undefined && input.reason.length > 0) body.reason = input.reason;
  const res = await callAdminApi<unknown>(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/retry-problematic`,
    body,
  });
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeRetryResult(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_retry_payload', body: null };
}

/**
 * The shared client only lifts `body.reason`; the provider routes answer
 * `{error, detail}` (e.g. 409 {error:'conflict', detail:'no_credential_on_account'}).
 * Prefer the specific `detail`, then `error`, so the UI can map it to Turkish.
 */
export function liftServerCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !res.reason.startsWith('http_')) return res;
  const detail = res.body?.detail;
  if (typeof detail === 'string' && detail.length > 0) return { ...res, reason: detail };
  const code = res.body?.error;
  if (typeof code === 'string' && code.length > 0) return { ...res, reason: code };
  return res;
}

// ---------------------------------------------------------------------------
// Normalisers (pure — unit-tested through the page tests)
// ---------------------------------------------------------------------------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function boolOrNull(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}
function rec(v: unknown): Rec {
  return isRec(v) ? v : {};
}
function arr(v: unknown): readonly unknown[] {
  return Array.isArray(v) ? v : [];
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}
function params(v: unknown): ProblemParams {
  const out: Record<string, string | number | null> = {};
  for (const [k, val] of Object.entries(rec(v))) {
    if (typeof val === 'string' || (typeof val === 'number' && Number.isFinite(val)) || val === null) {
      out[k] = val;
    }
  }
  return out;
}

const SEVERITIES: readonly OverviewSeverity[] = ['critical', 'warn', 'info'];
const PROVIDER_STATUSES: readonly ProviderHealthStatus[] = ['ok', 'warn', 'critical', 'idle'];
const GATEWAY_STATUSES: readonly GatewayHealthStatus[] = ['ok', 'down', 'unknown'];

function normalizeReasons(v: unknown): readonly OverviewReasonCount[] {
  return arr(v)
    .filter(isRec)
    .map((r) => ({ code: str(r.code, 'unknown'), count: num(r.count) }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
}

function normalizeProvider(raw: Rec): OverviewProvider | null {
  const providerId = strOrNull(raw.providerId);
  if (!providerId) return null;
  const a = rec(raw.accounts);
  const s = rec(raw.stock);
  const o24 = rec(raw.orders24h);
  const o7 = rec(raw.orders7d);
  const renewal = isRec(raw.renewal24h)
    ? { completed: num(raw.renewal24h.completed), failed: num(raw.renewal24h.failed) }
    : null;
  const gateway = isRec(raw.gateway)
    ? { status: oneOf(raw.gateway.status, GATEWAY_STATUSES, 'unknown'), since: strOrNull(raw.gateway.since) }
    : null;
  return {
    providerId,
    label: str(raw.label, providerId),
    kind: raw.kind === 'alias' ? 'alias' : 'mailbox',
    onSale: raw.onSale === true,
    activeDomains: num(raw.activeDomains),
    accounts: {
      total: num(a.total),
      active: num(a.active),
      verified: num(a.verified),
      pending: num(a.pending),
      loginFailed: num(a.loginFailed),
      sticky: num(a.sticky),
      disabled: num(a.disabled),
      suspended: num(a.suspended),
      noCredential: num(a.noCredential),
      lastVerifiedAt: strOrNull(a.lastVerifiedAt),
    },
    quarantineReasons: normalizeReasons(raw.quarantineReasons),
    stock: {
      displayed: num(s.displayed),
      sellable: num(s.sellable),
      rawAvailable: num(s.rawAvailable),
      inFlight: num(s.inFlight),
    },
    orders24h: { total: num(o24.total), delivered: num(o24.delivered), cancelled: num(o24.cancelled) },
    orders7d: { total: num(o7.total), delivered: num(o7.delivered) },
    lastDeliveryAt: strOrNull(raw.lastDeliveryAt),
    renewal24h: renewal,
    gateway,
    status: oneOf(raw.status, PROVIDER_STATUSES, 'idle'),
  };
}

function normalizeAction(v: unknown): OverviewProblemAction {
  if (!isRec(v)) return null;
  if (v.kind === 'retry_problematic' && typeof v.providerId === 'string' && v.providerId.length > 0) {
    return { kind: 'retry_problematic', providerId: v.providerId };
  }
  if (v.kind === 'link' && typeof v.href === 'string' && v.href.length > 0) {
    return { kind: 'link', href: v.href };
  }
  return null;
}

export function normalizeOverview(raw: unknown): OverviewResponse | null {
  if (!isRec(raw) || typeof raw.generatedAt !== 'string' || !Array.isArray(raw.providers)) return null;
  const sys = rec(raw.system);
  const o24 = rec(sys.orders24h);
  const stuck = rec(sys.stuckOrders);
  const webshare = rec(sys.webshare);
  return {
    generatedAt: raw.generatedAt,
    cacheAgeSec: num(raw.cacheAgeSec),
    system: {
      orders24h: {
        total: num(o24.total),
        delivered: num(o24.delivered),
        cancelled: num(o24.cancelled),
        open: num(o24.open),
      },
      ordersDaily7d: arr(sys.ordersDaily7d)
        .filter(isRec)
        .map((d) => ({
          day: str(d.day),
          total: num(d.total),
          delivered: num(d.delivered),
          cancelled: num(d.cancelled),
        }))
        .sort((x, y) => (x.day < y.day ? -1 : x.day > y.day ? 1 : 0)),
      stuckOrders: { expiredLease: num(stuck.expiredLease), oldReserved: num(stuck.oldReserved) },
      webshare: {
        poolSize: numOrNull(webshare.poolSize),
        status: oneOf(webshare.status, ['ok', 'warn', 'down', 'unknown'] as const, 'unknown'),
      },
      runtime: { metricsFresh: boolOrNull(rec(sys.runtime).metricsFresh) },
      notifier: { emailConfigured: rec(sys.notifier).emailConfigured === true },
      metricsAvailable: sys.metricsAvailable === true,
    },
    gateways: arr(raw.gateways)
      .filter(isRec)
      .map((g) => ({
        providerIds: arr(g.providerIds).filter((p): p is string => typeof p === 'string'),
        host: str(g.host),
        port: num(g.port),
        status: oneOf(g.status, GATEWAY_STATUSES, 'unknown'),
        since: strOrNull(g.since),
        lastOkAt: strOrNull(g.lastOkAt),
        source: oneOf(g.source, ['canary', 'probes', 'none'] as const, 'none'),
      })),
    providers: raw.providers
      .filter(isRec)
      .map(normalizeProvider)
      .filter((p): p is OverviewProvider => p !== null),
    problems: arr(raw.problems)
      .filter(isRec)
      .map((p, i) => ({
        id: str(p.id, `problem-${i}`),
        severity: oneOf(p.severity, SEVERITIES, 'warn'),
        code: str(p.code, 'unknown'),
        providerId: strOrNull(p.providerId),
        params: params(p.params),
        since: strOrNull(p.since),
        action: normalizeAction(p.action),
      })),
  };
}

export function normalizeAlarms(raw: unknown): OverviewAlarmsResponse | null {
  if (!isRec(raw) || !Array.isArray(raw.alarms) || typeof raw.evaluatorAlive !== 'boolean') return null;
  const notifier = rec(raw.notifier);
  return {
    evaluatedAt: strOrNull(raw.evaluatedAt),
    evaluatorAlive: raw.evaluatorAlive,
    alarms: raw.alarms
      .filter(isRec)
      .filter((a) => a.severity === 'critical' || a.severity === 'warn')
      .map((a, i) => ({
        id: str(a.id, `alarm-${i}`),
        severity: a.severity === 'critical' ? ('critical' as const) : ('warn' as const),
        code: str(a.code, 'unknown'),
        providerIds: arr(a.providerIds).filter((p): p is string => typeof p === 'string'),
        since: strOrNull(a.since),
        params: params(a.params),
      })),
    notifier: {
      emailConfigured: notifier.emailConfigured === true,
      lastSentAt: strOrNull(notifier.lastSentAt),
    },
  };
}

export function normalizeRetryResult(raw: unknown): RetryProblematicResult | null {
  if (!isRec(raw) || typeof raw.dryRun !== 'boolean') return null;
  if (raw.dryRun) {
    return {
      dryRun: true,
      candidates: num(raw.candidates),
      sticky: num(raw.sticky),
      skippedInFlight: num(raw.skippedInFlight),
      noCredential: num(raw.noCredential),
    };
  }
  return {
    dryRun: false,
    requested: num(raw.requested),
    nudged: num(raw.nudged),
    skippedInFlight: num(raw.skippedInFlight),
  };
}
